import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  getDocs,
  collection,
  orderBy,
  query,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import {
  FaChartBar,
  FaUsers,
  FaExclamationTriangle,
  FaCheckCircle,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaFilter,
  FaExternalLinkAlt,
  FaChartLine,
  FaExchangeAlt,
  FaUpload,
  FaCloudUploadAlt,
  FaFileCsv,
  FaFileExcel,
  FaTimes,
  FaEye,
  FaTrashAlt,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import "./QASprintAnalytics.css";

// Color palette for charts
const COLORS = ["#7F7FD5", "#E786D7", "#86B7E7", "#E7A686", "#86E7A6", "#E7E786", "#A686E7"];
const FORWARD_COLOR = "#28a745";
const BACK_COLOR = "#dc3545";

/**
 * Parse CSV data from sprint document
 * Handles both newline-separated and space-separated (URL-based) formats
 * @param {string} csvData - Raw CSV string
 * @returns {Array} Parsed ticket data
 */
const parseCSVData = (csvData) => {
  if (!csvData) return [];
  
  let lines = [];
  
  // Check if data uses newlines or spaces as row separators
  if (csvData.includes("\n")) {
    // Standard newline-separated CSV
    lines = csvData.trim().split("\n");
  } else {
    // Space-separated format where each row starts with https://
    // First, extract the header
    const headerMatch = csvData.match(/^([^h]+?)(?=https:)/);
    const header = headerMatch ? headerMatch[1].trim() : "Ticket_URL,Developer,Direction,Reason Text,Reason Simplified,Fault";
    
    // Split by https:// and reconstruct URLs
    const parts = csvData.split(/(?=https:\/\/)/);
    lines = [header];
    
    parts.forEach(part => {
      if (part.startsWith("https://")) {
        lines.push(part.trim());
      }
    });
  }
  
  if (lines.length < 2) return [];
  
  const tickets = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Parse CSV line, handling the URL which contains no commas
    const values = line.split(",").map(v => v.trim());
    
    if (values.length >= 3) {
      tickets.push({
        ticketUrl: values[0] || "",
        developer: values[1] ? values[1].charAt(0).toUpperCase() + values[1].slice(1).toLowerCase() : "Unknown",
        direction: values[2] || "",
        reasonText: values[3] || "",
        reasonSimplified: values[4] || "",
        fault: values[5] || "",
      });
    }
  }
  
  return tickets;
};

/**
 * Calculate statistics from ticket data
 * @param {Array} tickets - Parsed ticket data
 * @returns {Object} Calculated statistics
 */
const calculateStatistics = (tickets) => {
  const totalTickets = tickets.length;
  const forwardTickets = tickets.filter(t => t.direction.toLowerCase() === "forward");
  const backTickets = tickets.filter(t => t.direction.toLowerCase() === "back");
  
  // Unique tickets (by URL)
  const uniqueTicketUrls = [...new Set(tickets.map(t => t.ticketUrl))];
  const uniqueTicketCount = uniqueTicketUrls.length;
  
  // Developer stats
  const developerStats = {};
  tickets.forEach(ticket => {
    const dev = ticket.developer;
    if (!developerStats[dev]) {
      developerStats[dev] = {
        name: dev,
        total: 0,
        forward: 0,
        back: 0,
        reasons: {},
        tickets: [],
      };
    }
    developerStats[dev].total++;
    developerStats[dev].tickets.push(ticket);
    
    if (ticket.direction.toLowerCase() === "forward") {
      developerStats[dev].forward++;
    } else if (ticket.direction.toLowerCase() === "back") {
      developerStats[dev].back++;
      if (ticket.reasonText) {
        developerStats[dev].reasons[ticket.reasonText] = 
          (developerStats[dev].reasons[ticket.reasonText] || 0) + 1;
      }
    }
  });
  
  // Calculate percentages for each developer
  Object.values(developerStats).forEach(dev => {
    dev.backPercentage = dev.total > 0 ? ((dev.back / dev.total) * 100).toFixed(1) : 0;
    dev.forwardPercentage = dev.total > 0 ? ((dev.forward / dev.total) * 100).toFixed(1) : 0;
  });
  
  // Reason breakdown
  const reasonBreakdown = {};
  backTickets.forEach(ticket => {
    const reason = ticket.reasonText || "Unspecified";
    reasonBreakdown[reason] = (reasonBreakdown[reason] || 0) + 1;
  });
  
  return {
    totalTickets,
    uniqueTicketCount,
    forwardCount: forwardTickets.length,
    backCount: backTickets.length,
    backPercentage: totalTickets > 0 ? ((backTickets.length / totalTickets) * 100).toFixed(1) : 0,
    forwardPercentage: totalTickets > 0 ? ((forwardTickets.length / totalTickets) * 100).toFixed(1) : 0,
    developerStats: Object.values(developerStats).sort((a, b) => b.total - a.total),
    reasonBreakdown,
  };
};

/**
 * Compare two sprints and calculate improvements/regressions
 * @param {Object} currentStats - Current sprint statistics
 * @param {Object} previousStats - Previous sprint statistics
 * @returns {Object} Comparison results
 */
const compareSprintStats = (currentStats, previousStats) => {
  if (!previousStats) return null;
  
  const backPercentageChange = parseFloat(currentStats.backPercentage) - parseFloat(previousStats.backPercentage);
  
  // Developer comparison
  const developerComparison = currentStats.developerStats.map(currentDev => {
    const prevDev = previousStats.developerStats.find(d => d.name === currentDev.name);
    return {
      name: currentDev.name,
      currentBackPercentage: parseFloat(currentDev.backPercentage),
      previousBackPercentage: prevDev ? parseFloat(prevDev.backPercentage) : null,
      change: prevDev ? parseFloat(currentDev.backPercentage) - parseFloat(prevDev.backPercentage) : null,
    };
  });
  
  return {
    backPercentageChange,
    isImproved: backPercentageChange < 0,
    developerComparison,
  };
};

const QASprintAnalyticsPage = () => {
  const [sprints, setSprints] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [comparisonSprint, setComparisonSprint] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [developerFilter, setDeveloperFilter] = useState("all");
  const [showComparison, setShowComparison] = useState(false);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCsvContent, setUploadCsvContent] = useState("");
  const [uploadDate, setUploadDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // YYYY-MM-DD
  });
  const [uploadPreview, setUploadPreview] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Fetch all sprints from Firestore
   */
  const fetchSprints = useCallback(async () => {
    if (!firestoreProd) {
      toast.error("Firebase is not initialized");
      return;
    }

    setIsLoading(true);

    try {
      const sprintsRef = collection(firestoreProd, "sprint-qa");
      // Try ordering by 'date' field (actual field name in DB)
      const q = query(sprintsRef, orderBy("date", "desc"));
      const snapshot = await getDocs(q);
      
      const sprintsData = snapshot.docs.map(doc => {
        const docData = doc.data();
        // Support both field names: 'data' (actual) and 'csvData' (alternative)
        const csvContent = docData.data || docData.csvData || "";
        const tickets = parseCSVData(csvContent);
        const statistics = calculateStatistics(tickets);
        
        // Support both field names: 'date' (actual) and 'sprintEndDate' (alternative)
        const dateField = docData.date || docData.sprintEndDate;
        const sprintDate = dateField?.toDate ? dateField.toDate() : new Date(dateField);
        
        return {
          id: doc.id,
          ...docData,
          tickets,
          statistics,
          sprintEndDate: sprintDate,
        };
      });
      
      setSprints(sprintsData);
      
      // Auto-select the most recent sprint
      if (sprintsData.length > 0) {
        setSelectedSprint(sprintsData[0]);
        if (sprintsData.length > 1) {
          setComparisonSprint(sprintsData[1]);
        }
      }
      
      setHasLoaded(true);
      toast.success(`Loaded ${sprintsData.length} sprint(s)`);
    } catch (error) {
      console.error("Error fetching sprints:", error);
      toast.error("Failed to fetch sprint data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSprints();
  }, [fetchSprints]);

  /**
   * Handle file selection (from input or drag-and-drop)
   * Supports CSV (.csv) and Excel (.xlsx, .xls) files
   */
  const handleFileSelect = useCallback((file) => {
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith(".csv");
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");
    
    if (!isCSV && !isExcel) {
      toast.error("Please upload a CSV (.csv) or Excel (.xlsx, .xls) file");
      return;
    }

    setUploadFile(file);

    if (isExcel) {
      // Read Excel file and convert to CSV
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          
          // Use the first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert sheet to CSV string
          const csvContent = XLSX.utils.sheet_to_csv(worksheet);
          setUploadCsvContent(csvContent);
          
          // Parse for preview
          const tickets = parseCSVData(csvContent);
          setUploadPreview(tickets);
          
          if (tickets.length === 0) {
            toast.warning("No ticket data found in the file. Please check the format.");
          } else {
            toast.success(`Parsed ${tickets.length} ticket(s) from "${firstSheetName}" sheet`);
            setShowPreview(true);
          }
        } catch (err) {
          console.error("Error parsing Excel file:", err);
          toast.error("Failed to parse Excel file. Please check the format.");
        }
      };
      reader.onerror = () => {
        toast.error("Failed to read file");
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Read CSV file as text
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        setUploadCsvContent(content);
        
        // Parse for preview
        const tickets = parseCSVData(content);
        setUploadPreview(tickets);
        
        if (tickets.length === 0) {
          toast.warning("No ticket data found in the file. Please check the CSV format.");
        } else {
          toast.success(`Parsed ${tickets.length} ticket(s) from file`);
          setShowPreview(true);
        }
      };
      reader.onerror = () => {
        toast.error("Failed to read file");
      };
      reader.readAsText(file);
    }
  }, []);

  /**
   * Handle drag events for the drop zone
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  /**
   * Upload CSV data to Firestore sprint-qa collection
   */
  const handleUploadSubmit = async () => {
    if (!uploadCsvContent.trim()) {
      toast.error("No CSV data to upload. Please select a file first.");
      return;
    }

    if (!uploadDate) {
      toast.error("Please select a sprint date.");
      return;
    }

    if (!firestoreProd) {
      toast.error("Firebase is not initialized");
      return;
    }

    setIsUploading(true);

    try {
      const sprintDate = new Date(uploadDate + "T00:00:00");
      const sprintsRef = collection(firestoreProd, "sprint-qa");
      
      await addDoc(sprintsRef, {
        data: uploadCsvContent.trim(),
        date: Timestamp.fromDate(sprintDate),
      });

      toast.success("Sprint data uploaded successfully!");
      
      // Reset upload state
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadCsvContent("");
      setUploadPreview([]);
      setShowPreview(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Refresh sprints list
      fetchSprints();
    } catch (error) {
      console.error("Error uploading sprint data:", error);
      toast.error("Failed to upload sprint data: " + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Reset upload modal state
   */
  const resetUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadCsvContent("");
    setUploadPreview([]);
    setShowPreview(false);
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Delete sprint state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sprintToDelete, setSprintToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Prompt user to confirm before deleting a sprint
   */
  const confirmDeleteSprint = (sprint) => {
    setSprintToDelete(sprint);
    setShowDeleteConfirm(true);
  };

  /**
   * Delete a sprint document from Firestore
   */
  const handleDeleteSprint = async () => {
    if (!sprintToDelete || !firestoreProd) return;

    setIsDeleting(true);

    try {
      const sprintDocRef = doc(firestoreProd, "sprint-qa", sprintToDelete.id);
      await deleteDoc(sprintDocRef);

      toast.success("Sprint deleted successfully!");

      // If the deleted sprint was selected, clear selection
      if (selectedSprint?.id === sprintToDelete.id) {
        setSelectedSprint(null);
      }
      if (comparisonSprint?.id === sprintToDelete.id) {
        setComparisonSprint(null);
      }

      // Reset delete state
      setShowDeleteConfirm(false);
      setSprintToDelete(null);

      // Refresh sprints list
      fetchSprints();
    } catch (error) {
      console.error("Error deleting sprint:", error);
      toast.error("Failed to delete sprint: " + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate comparison data
  const comparisonData = useMemo(() => {
    if (!selectedSprint || !comparisonSprint || !showComparison) return null;
    return compareSprintStats(selectedSprint.statistics, comparisonSprint.statistics);
  }, [selectedSprint, comparisonSprint, showComparison]);

  // Prepare chart data for developer performance
  const developerChartData = useMemo(() => {
    if (!selectedSprint) return [];
    return selectedSprint.statistics.developerStats.map(dev => ({
      name: dev.name,
      Forward: dev.forward,
      Back: dev.back,
      "Back %": parseFloat(dev.backPercentage),
    }));
  }, [selectedSprint]);

  // Prepare pie chart data for direction breakdown
  const directionPieData = useMemo(() => {
    if (!selectedSprint) return [];
    return [
      { name: "Forward", value: selectedSprint.statistics.forwardCount, color: FORWARD_COLOR },
      { name: "Back", value: selectedSprint.statistics.backCount, color: BACK_COLOR },
    ];
  }, [selectedSprint]);

  // Prepare pie chart data for reason breakdown
  const reasonPieData = useMemo(() => {
    if (!selectedSprint) return [];
    return Object.entries(selectedSprint.statistics.reasonBreakdown).map(([reason, count], index) => ({
      name: reason,
      value: count,
      color: COLORS[index % COLORS.length],
    }));
  }, [selectedSprint]);

  // Prepare trend data for sprint comparison
  const trendData = useMemo(() => {
    return sprints.slice().reverse().map(sprint => ({
      name: formatSprintDate(sprint.sprintEndDate),
      "Back %": parseFloat(sprint.statistics.backPercentage),
      "Total Tickets": sprint.statistics.totalTickets,
      "Back Count": sprint.statistics.backCount,
    }));
  }, [sprints]);

  // Filter tickets by developer
  const filteredTickets = useMemo(() => {
    if (!selectedSprint) return [];
    if (developerFilter === "all") return selectedSprint.tickets;
    return selectedSprint.tickets.filter(t => t.developer === developerFilter);
  }, [selectedSprint, developerFilter]);

  // Get unique developers for filter
  const developers = useMemo(() => {
    if (!selectedSprint) return [];
    return [...new Set(selectedSprint.tickets.map(t => t.developer))].sort();
  }, [selectedSprint]);

  /**
   * Format sprint date for display
   */
  function formatSprintDate(date) {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  /**
   * Get change indicator icon
   */
  const getChangeIndicator = (change) => {
    if (change === null || change === undefined) return <FaMinus className="text-muted" />;
    if (change < 0) return <FaArrowDown className="text-success" />;
    if (change > 0) return <FaArrowUp className="text-danger" />;
    return <FaMinus className="text-muted" />;
  };

  /**
   * Extract ticket ID from URL
   */
  const getTicketId = (url) => {
    if (!url) return "N/A";
    const match = url.match(/DEV-\d+/);
    return match ? match[0] : url;
  };

  return (
    <div className="container-fluid p-4 QASprintAnalyticsPageContainer">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h1>
              <FaChartBar className="me-2" />
              QA Sprint Analytics
            </h1>
            <p className="text-muted">
              Track QA performance, developer metrics, and sprint-over-sprint improvements
            </p>
          </div>
          <button
            className="btn btn-primary upload-sprint-btn"
            onClick={() => setShowUploadModal(true)}
          >
            <FaUpload className="me-2" />
            Upload Sprint Data
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={resetUploadModal}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h4>
                <FaCloudUploadAlt className="me-2" />
                Upload Sprint QA Data
              </h4>
              <button className="btn-close-modal" onClick={resetUploadModal}>
                <FaTimes />
              </button>
            </div>

            <div className="upload-modal-body">
              {/* Date Picker */}
              <div className="mb-3">
                <label htmlFor="sprintDate" className="form-label fw-bold">
                  Sprint End Date
                </label>
                <input
                  type="date"
                  id="sprintDate"
                  className="form-control"
                  value={uploadDate}
                  onChange={(e) => setUploadDate(e.target.value)}
                />
              </div>

              {/* Drag and Drop Zone */}
              <div
                className={`drop-zone ${isDragOver ? "drag-over" : ""} ${uploadFile ? "has-file" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                />
                
                {uploadFile ? (
                  <div className="drop-zone-file-info">
                    {uploadFile.name.match(/\.xlsx?$/i) ? (
                      <FaFileExcel className="file-icon" style={{ color: "#217346" }} />
                    ) : (
                      <FaFileCsv className="file-icon" />
                    )}
                    <div className="file-details">
                      <span className="file-name">{uploadFile.name}</span>
                      <span className="file-size">
                        {(uploadFile.size / 1024).toFixed(1)} KB &bull; {uploadPreview.length} ticket(s) found
                      </span>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadFile(null);
                        setUploadCsvContent("");
                        setUploadPreview([]);
                        setShowPreview(false);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="drop-zone-prompt">
                    <FaCloudUploadAlt className="upload-icon" />
                    <p className="drop-zone-text">
                      Drag & drop your file here
                    </p>
                    <p className="drop-zone-subtext">
                      or click to browse files
                    </p>
                    <span className="drop-zone-hint">
                      Supports .csv, .xlsx, and .xls files
                    </span>
                  </div>
                )}
              </div>

              {/* CSV Format Hint */}
              <div className="csv-format-hint mt-3">
                <small className="text-muted">
                  <strong>Expected CSV format:</strong> Ticket_URL, Developer, Direction, Reason Text, Reason Simplified, Fault
                </small>
              </div>

              {/* Preview Toggle */}
              {uploadPreview.length > 0 && (
                <div className="mt-3">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    <FaEye className="me-1" />
                    {showPreview ? "Hide Preview" : "Show Preview"}
                  </button>
                </div>
              )}

              {/* Data Preview */}
              {showPreview && uploadPreview.length > 0 && (
                <div className="upload-preview mt-3">
                  <h6 className="mb-2">
                    Data Preview ({uploadPreview.length} tickets)
                  </h6>
                  <div className="table-responsive" style={{ maxHeight: "250px", overflowY: "auto" }}>
                    <table className="table table-sm table-hover">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>#</th>
                          <th>Ticket</th>
                          <th>Developer</th>
                          <th>Direction</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadPreview.slice(0, 50).map((ticket, index) => (
                          <tr key={index}>
                            <td className="text-muted">{index + 1}</td>
                            <td>
                              <small>{ticket.ticketUrl ? ticket.ticketUrl.replace(/https?:\/\/[^/]+\//, "").substring(0, 30) : "N/A"}</small>
                            </td>
                            <td>{ticket.developer}</td>
                            <td>
                              <span className={`badge ${ticket.direction.toLowerCase() === "forward" ? "bg-success" : "bg-danger"}`}>
                                {ticket.direction}
                              </span>
                            </td>
                            <td><small>{ticket.reasonText || "-"}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {uploadPreview.length > 50 && (
                      <p className="text-muted text-center mt-1">
                        <small>Showing first 50 of {uploadPreview.length} tickets</small>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="upload-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={resetUploadModal}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUploadSubmit}
                disabled={isUploading || !uploadFile || !uploadDate}
              >
                {isUploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaUpload className="me-2" />
                    Upload to Firebase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && sprintToDelete && (
        <div className="upload-modal-overlay" onClick={() => { setShowDeleteConfirm(false); setSprintToDelete(null); }}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h4>
                <FaTrashAlt className="me-2" style={{ color: "#dc3545" }} />
                Delete Sprint Data
              </h4>
              <button className="btn-close-modal" onClick={() => { setShowDeleteConfirm(false); setSprintToDelete(null); }}>
                <FaTimes />
              </button>
            </div>
            <div className="upload-modal-body">
              <div className="delete-warning">
                <FaExclamationTriangle className="warning-icon" />
                <p>Are you sure you want to delete this sprint?</p>
              </div>
              <div className="delete-sprint-info">
                <p><strong>Sprint Date:</strong> {formatSprintDate(sprintToDelete.sprintEndDate)}</p>
                <p><strong>Total Tickets:</strong> {sprintToDelete.statistics.totalTickets}</p>
                <p><strong>Back Rate:</strong> {sprintToDelete.statistics.backPercentage}%</p>
              </div>
              <p className="text-danger" style={{ fontSize: "0.85rem" }}>
                This action cannot be undone. The sprint data will be permanently removed from the database.
              </p>
            </div>
            <div className="upload-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowDeleteConfirm(false); setSprintToDelete(null); }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteSprint}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrashAlt className="me-2" />
                    Delete Sprint
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint Selection */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">
            <FaFilter className="me-2" />
            Sprint Selection
          </h5>
        </div>
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-4">
              <label htmlFor="sprintSelect" className="form-label">
                Select Sprint
              </label>
              <div className="d-flex gap-2">
                <select
                  id="sprintSelect"
                  className="form-select"
                  value={selectedSprint?.id || ""}
                  onChange={(e) => {
                    const sprint = sprints.find(s => s.id === e.target.value);
                    setSelectedSprint(sprint);
                  }}
                  disabled={isLoading}
                >
                  <option value="">Select a sprint...</option>
                  {sprints.map(sprint => (
                    <option key={sprint.id} value={sprint.id}>
                      Sprint ending {formatSprintDate(sprint.sprintEndDate)} ({sprint.statistics.totalTickets} tickets)
                    </option>
                  ))}
                </select>
                {selectedSprint && (
                  <button
                    className="btn btn-outline-danger btn-delete-sprint"
                    onClick={() => confirmDeleteSprint(selectedSprint)}
                    title="Delete this sprint"
                    disabled={isLoading}
                  >
                    <FaTrashAlt />
                  </button>
                )}
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showComparison"
                  checked={showComparison}
                  onChange={(e) => setShowComparison(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showComparison">
                  <FaExchangeAlt className="me-1" />
                  Compare with previous sprint
                </label>
              </div>
              {showComparison && (
                <select
                  className="form-select"
                  value={comparisonSprint?.id || ""}
                  onChange={(e) => {
                    const sprint = sprints.find(s => s.id === e.target.value);
                    setComparisonSprint(sprint);
                  }}
                  disabled={isLoading}
                >
                  <option value="">Select comparison sprint...</option>
                  {sprints.filter(s => s.id !== selectedSprint?.id).map(sprint => (
                    <option key={sprint.id} value={sprint.id}>
                      Sprint ending {formatSprintDate(sprint.sprintEndDate)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-md-4">
              <label htmlFor="developerFilter" className="form-label">
                Filter by Developer
              </label>
              <select
                id="developerFilter"
                className="form-select"
                value={developerFilter}
                onChange={(e) => setDeveloperFilter(e.target.value)}
                disabled={!selectedSprint}
              >
                <option value="all">All Developers</option>
                {developers.map(dev => (
                  <option key={dev} value={dev}>{dev}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading sprint data...</p>
        </div>
      )}

      {!isLoading && hasLoaded && !selectedSprint && (
        <div className="alert alert-info">
          No sprint data available. Please add sprint QA data to the "sprint-qa" collection.
        </div>
      )}

      {selectedSprint && (
        <>
          {/* Statistics Cards */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card stat-card">
                <div className="card-body text-center">
                  <div className="stat-icon">
                    <FaChartBar />
                  </div>
                  <h5 className="card-title">Total Tickets</h5>
                  <h2 className="stat-value">{selectedSprint.statistics.totalTickets}</h2>
                  <small className="text-muted">
                    {selectedSprint.statistics.uniqueTicketCount} unique
                  </small>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card stat-card success">
                <div className="card-body text-center">
                  <div className="stat-icon success">
                    <FaCheckCircle />
                  </div>
                  <h5 className="card-title">Forward</h5>
                  <h2 className="stat-value text-success">
                    {selectedSprint.statistics.forwardCount}
                  </h2>
                  <small className="text-muted">
                    {selectedSprint.statistics.forwardPercentage}% of total
                  </small>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card stat-card danger">
                <div className="card-body text-center">
                  <div className="stat-icon danger">
                    <FaExclamationTriangle />
                  </div>
                  <h5 className="card-title">Sent Back</h5>
                  <h2 className="stat-value text-danger">
                    {selectedSprint.statistics.backCount}
                  </h2>
                  <small className="text-muted">
                    {selectedSprint.statistics.backPercentage}% of total
                  </small>
                  {showComparison && comparisonData && (
                    <div className="comparison-badge mt-2">
                      {getChangeIndicator(comparisonData.backPercentageChange)}
                      <span className={comparisonData.backPercentageChange < 0 ? "text-success" : "text-danger"}>
                        {comparisonData.backPercentageChange > 0 ? "+" : ""}
                        {comparisonData.backPercentageChange.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card stat-card">
                <div className="card-body text-center">
                  <div className="stat-icon">
                    <FaUsers />
                  </div>
                  <h5 className="card-title">Developers</h5>
                  <h2 className="stat-value">
                    {selectedSprint.statistics.developerStats.length}
                  </h2>
                  <small className="text-muted">
                    contributors
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="row mb-4">
            {/* Direction Breakdown Pie Chart */}
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="mb-0">Direction Breakdown</h5>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={directionPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {directionPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Reason Breakdown Pie Chart */}
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="mb-0">Reasons for Sending Back</h5>
                </div>
                <div className="card-body">
                  {reasonPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={reasonPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {reasonPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-muted py-5">
                      No tickets sent back
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sprint Trend Line Chart */}
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="mb-0">
                    <FaChartLine className="me-2" />
                    Sprint Trend
                  </h5>
                </div>
                <div className="card-body">
                  {trendData.length > 1 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="Back %" 
                          stroke={BACK_COLOR} 
                          strokeWidth={2}
                          dot={{ fill: BACK_COLOR }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-muted py-5">
                      Need more sprints for trend analysis
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Developer Performance */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    <FaUsers className="me-2" />
                    Developer Performance
                  </h5>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={developerChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="Forward" fill={FORWARD_COLOR} name="Forward" />
                      <Bar yAxisId="left" dataKey="Back" fill={BACK_COLOR} name="Back" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Developer Stats Table */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Developer Statistics</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Developer</th>
                          <th className="text-center">Total</th>
                          <th className="text-center">Forward</th>
                          <th className="text-center">Back</th>
                          <th className="text-center">Back %</th>
                          {showComparison && comparisonData && <th className="text-center">Change</th>}
                          <th>Top Reasons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSprint.statistics.developerStats.map(dev => {
                          const comparison = comparisonData?.developerComparison.find(d => d.name === dev.name);
                          const topReasons = Object.entries(dev.reasons)
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 3);
                          
                          return (
                            <tr key={dev.name}>
                              <td>
                                <strong>{dev.name}</strong>
                              </td>
                              <td className="text-center">{dev.total}</td>
                              <td className="text-center text-success">{dev.forward}</td>
                              <td className="text-center text-danger">{dev.back}</td>
                              <td className="text-center">
                                <span className={`badge ${parseFloat(dev.backPercentage) > 30 ? 'bg-danger' : parseFloat(dev.backPercentage) > 15 ? 'bg-warning' : 'bg-success'}`}>
                                  {dev.backPercentage}%
                                </span>
                              </td>
                              {showComparison && comparisonData && (
                                <td className="text-center">
                                  {comparison?.change !== null ? (
                                    <span className={comparison.change < 0 ? "text-success" : comparison.change > 0 ? "text-danger" : "text-muted"}>
                                      {getChangeIndicator(comparison.change)}
                                      {comparison.change > 0 ? "+" : ""}{comparison.change?.toFixed(1)}%
                                    </span>
                                  ) : (
                                    <span className="text-muted">New</span>
                                  )}
                                </td>
                              )}
                              <td>
                                {topReasons.length > 0 ? (
                                  topReasons.map(([reason, count], idx) => (
                                    <span key={reason} className="badge bg-secondary me-1">
                                      {reason} ({count})
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket Details Table */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Ticket Details</h5>
                  <span className="badge bg-primary">{filteredTickets.length} tickets</span>
                </div>
                <div className="card-body">
                  <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <table className="table table-hover table-sm">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Ticket</th>
                          <th>Developer</th>
                          <th>Direction</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTickets.map((ticket, index) => (
                          <tr key={`${ticket.ticketUrl}-${index}`}>
                            <td>
                              <a 
                                href={ticket.ticketUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="ticket-link"
                              >
                                {getTicketId(ticket.ticketUrl)}
                                <FaExternalLinkAlt className="ms-1" size={10} />
                              </a>
                            </td>
                            <td>{ticket.developer}</td>
                            <td>
                              <span className={`badge ${ticket.direction.toLowerCase() === 'forward' ? 'bg-success' : 'bg-danger'}`}>
                                {ticket.direction}
                              </span>
                            </td>
                            <td>{ticket.reasonText || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Sprint Comparison Summary */}
      {showComparison && comparisonData && selectedSprint && comparisonSprint && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <FaExchangeAlt className="me-2" />
                  Sprint Comparison Summary
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Current Sprint: {formatSprintDate(selectedSprint.sprintEndDate)}</h6>
                    <ul className="list-unstyled">
                      <li>Total Tickets: {selectedSprint.statistics.totalTickets}</li>
                      <li>Back Rate: {selectedSprint.statistics.backPercentage}%</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <h6>Previous Sprint: {formatSprintDate(comparisonSprint.sprintEndDate)}</h6>
                    <ul className="list-unstyled">
                      <li>Total Tickets: {comparisonSprint.statistics.totalTickets}</li>
                      <li>Back Rate: {comparisonSprint.statistics.backPercentage}%</li>
                    </ul>
                  </div>
                </div>
                <hr />
                <div className="text-center">
                  <h4>
                    {comparisonData.isImproved ? (
                      <span className="text-success">
                        <FaArrowDown className="me-2" />
                        Improved by {Math.abs(comparisonData.backPercentageChange).toFixed(1)}%
                      </span>
                    ) : comparisonData.backPercentageChange > 0 ? (
                      <span className="text-danger">
                        <FaArrowUp className="me-2" />
                        Regressed by {comparisonData.backPercentageChange.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-muted">
                        <FaMinus className="me-2" />
                        No change
                      </span>
                    )}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QASprintAnalyticsPage;
